/**
 * CÀI ĐẶT THIẾT BỊ — lát 1–4 (YC-12).
 *
 * ⛔ MỘT LUẬT QUYẾT ĐỊNH CẢ MÀN HÌNH NÀY: **chỉ gửi những trường người dùng THẬT SỰ SỬA.**
 *
 *   `DeviceSettings` là bản tin GHI TỪNG PHẦN. Gửi thừa một trường không phải là "gửi dư cho
 *   chắc" — nó là một LỆNH THẬT: `modbusModel=0` gỡ công tơ, `elrModel=0` gỡ ELR,
 *   `elSetMa=0` tắt chống rò, `protectEn=0` tắt sạch bảo vệ. Đó là lý do cả dự án phải tách
 *   `DeviceSettings` ra một file proto2 riêng (YC-14) — để phân biệt được "không nói gì" với
 *   "nói số 0". Màn hình này mà gửi cả 14 trường thì công sức đó thành vô nghĩa.
 *
 *   ⇒ Mọi ô nhập đều đi qua `dat()`, và `dat()` chỉ đưa vào gói những khoá nằm trong `sua`.
 *
 * ⛔ KHÔNG BAO GIỜ ghi ngược cái vừa nhận. Tủ echo sau mỗi lần ghi; nếu màn hình lấy echo rồi
 *   gửi lại thì hai bên đá nhau vô hạn. Echo chỉ dùng để HIỂN THỊ và để so xem tủ có từ chối.
 */
import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { ChevronDown, Clock, Gauge, ShieldCheck, Zap } from 'lucide-react';
import { api, errorMessage } from '../lib/api';
import { DAY_THANH_DUOI } from './Layout';
import {
  docSoPha, datSoPha, datLop, lopDangBat, lopDangTat, coDauHieuQuenBat, khaiDuocLopTat,
  BIT_CHET_TRONG_PROTECT_EN, bitChetDangBat,
  NHAN_SO_PHA, chuaDatSoPha, LOP_BAO_VE_BIT, type SoPha,
} from '../lib/baove';
import {
  Badge, Button, Card, ConfirmDialog, ErrorBox, Field, SectionTitle, Spinner, Metric, LOP_CHUA_RO,
} from './ui';
import { useConTrongHan } from '../lib/useHanSuDung';
import { CAU_HET_HAN } from '../lib/hansudung';
import { nghiaSoKhong } from '../lib/sokhong';
import { docSoConTuoi, veDuocSoDo, cauKhongVeSoDo, moTaDoTuoiMs } from '../lib/tuoisodo';
import { fmtTime } from '../lib/format';
import { ChiTietKyThuat } from './ChiTietKyThuat';

/* ------------------------------------------------------------------ kiểu */

type Patch = Record<string, number>;

interface KetQuaLenh {
  guiHex: string;
  echo: Record<string, number>;
  cachGhep: 'khop' | 'tu-khoi-xuong' | 'khac-so' | null;
  soYeuCau: number;
  thieu: string[];
  lech: { truong: string; xin: number; thucTe: number }[];
  patch?: Patch;
}

interface LiveState {
  echo: any;
  cachGhep: KetQuaLenh['cachGhep'];
  /**
   * YC-34 vòng 2 — MÁY CHỦ chấm tuổi khung này từ `atMs` tủ tự đóng dấu
   * (`kapp_v2.proto:321`). `undefined` = máy chủ đời cũ ⇒ xử như CHƯA BIẾT, không xử như
   * "còn dùng được" (L62).
   */
  soDo?:
    | { ve: true; tuoiMs: number; hanConLaiMs: number }
    | { ve: false; vi: string; tuoiMs: number | null; cau: string; hanConLaiMs: 0 };
}

interface TrangThaiTu {
  echo: any;              // CabinetState
  gioMayChuMs: number;
  cachGhep: KetQuaLenh['cachGhep'];
}

/** Một ô cấu hình. `dai` chỉ dùng để CHẶN Ở MÀN HÌNH, không phải để kẹp giá trị. */
interface O {
  ten: string;
  nhan: string;
  donVi?: string;
  goiY?: string;
  min?: number;
  max?: number;
  /** `0` có nghĩa nguy hiểm — phải hỏi lại trước khi gửi. */
  canhBaoSo0?: string;
  /** Danh sách chọn thay cho ô số. */
  chon?: { gt: number; nhan: string }[];
}

/**
 * 🔴 `ma` LÀ ĐỊNH DANH TRONG MÃ, `tieuDe` LÀ CHỮ CHO NGƯỜI ĐỌC. Hai thứ khác nhau.
 *
 * "Lát 1", "lát 2" là **từ vựng nội bộ của nhóm làm** — nó ra đời từ cách chia việc ở YC-12,
 * không phải từ cái người vận hành đang làm. Người mở tủ lúc 2 giờ sáng không biết "lát" là gì,
 * và **số thứ tự của việc chúng tôi làm không phải tên của chức năng họ dùng**.
 *
 * ⇒ Chỉ đổi chuỗi hiển thị. `ma: 'lat1'…'lat4'` GIỮ NGUYÊN — cùng luật đã dùng cho
 *   `Route`/`routeId` khi UI đổi sang gọi là "Con đường" (YC-21 vòng 2 mục C3).
 *   Đổi định danh là churn không mua được gì, và làm gãy mọi chỗ đang tham chiếu tới nó.
 *
 * ⚠ 04/09/2026 (YC-33) — CHỮ HIỂN THỊ ẤY ĐỔI LẦN THỨ HAI: nay là **"Tuyến chiếu sáng"**.
 *   `Route`/`routeId`/`/con-duong` VẪN NGUYÊN, và đó chính là điều luật trên nói: chữ đổi
 *   được hai lần trong hai tuần mà không ai phải sửa một định danh nào.
 *
 * ⛔ Câu *"UI đổi sang gọi là 'Con đường'"* ở trên GIỮ NGUYÊN CHỮ CŨ CÓ CHỦ Ý. Nó ghi một
 *   quyết định đã xảy ra ngày ấy; sửa nó thành "Tuyến chiếu sáng" là viết lại lịch sử để
 *   một phép `grep` được xanh. Chú thích lịch sử không phải chỗ để dọn từ vựng.
 *
 * `moTa` giữ nguyên: câu *"Chọn 'Không lắp' là tủ ngừng đọc thiết bị đó"* là câu **dạy việc**,
 * không phải chữ trang trí.
 */
const LAT: { ma: string; tieuDe: string; Icon: ComponentType<{ className?: string }>; moTa: string; o: O[] }[] = [
  {
    ma: 'lat1', tieuDe: 'Thiết bị đo', Icon: Gauge, moTa: 'Công tơ và thiết bị chống rò. Chọn "Không lắp" là tủ ngừng đọc thiết bị đó.',
    o: [
      { ten: 'modbusModel', nhan: 'Công tơ', canhBaoSo0: 'Chọn "Không lắp" nghĩa là TỦ NGỪNG ĐỌC CÔNG TƠ. Số liệu điện năng sẽ không còn về nữa.',
        /*
         * ➕ 04/09/2026 — `3` = **KWS-AC301L** (dev_device giao, NV66/NV70).
         *
         * ⚠ Thứ tự và CON SỐ là hợp đồng, không phải thứ tự hiển thị: firmware đọc **giá trị**
         *   `modbusModel`, nên `3` phải là `3` ở mọi chỗ. Đây cũng là lý do `Không lắp` bắt
         *   buộc đứng đầu với `gt: 0` — cùng luật lee đã đặt cho dropdown trên màn hình tủ.
         *
         * ⛔ Máy chủ KHÔNG kiểm dải trị của `modbusModel` — `locPatchDeviceSettings` chỉ lọc
         *   theo TÊN trường (`codec.ts:263`). Nên danh sách này là hàng rào DUY NHẤT phía app,
         *   và một số lạ ở đây đi thẳng xuống tủ. Thêm dòng nào cũng phải có nguồn từ firmware.
         */
        chon: [{ gt: 0, nhan: 'Không lắp' }, { gt: 1, nhan: 'MFM383A' }, { gt: 2, nhan: 'MFM300' }, { gt: 3, nhan: 'KWS-AC301L' }] },
      { ten: 'modbusSlaveAdd', nhan: 'Địa chỉ công tơ', min: 1, max: 247 },
      { ten: 'elrModel', nhan: 'Thiết bị chống rò', canhBaoSo0: 'Chọn "Không lắp" nghĩa là TỦ NGỪNG ĐỌC ELR. Bản thân ELR vẫn cắt bằng mạch riêng, nhưng tủ sẽ không còn nhìn thấy nó.',
        chon: [{ gt: 0, nhan: 'Không lắp' }, { gt: 1, nhan: 'ELR-19N-M1' }] },
      { ten: 'elrSlaveAdd', nhan: 'Địa chỉ ELR', min: 1, max: 247 },
      { ten: 'modbusBaudrate', nhan: 'Tốc độ RS485', donVi: 'baud' },
      { ten: 'modbusParity', nhan: 'Parity', goiY: 'Dùng CHUNG cho cả cổng RS485',
        chon: [{ gt: 0, nhan: 'None' }, { gt: 1, nhan: 'Even' }, { gt: 2, nhan: 'Odd' }] },
    ],
  },
  {
    ma: 'lat3', tieuDe: 'Chống rò điện', Icon: Zap, moTa: 'Ngưỡng và thời gian trễ của điều khiển chống rò.',
    o: [
      { ten: 'elSetMa', nhan: 'Ngưỡng dòng rò', donVi: 'mA', min: 10, max: 99, goiY: 'Mặc định 30 mA. Đặt 0 = TẮT điều khiển chống rò.',
        canhBaoSo0: 'Đặt ngưỡng dòng rò = 0 nghĩa là TẮT ĐIỀU KHIỂN CHỐNG RÒ. Đây là công tắc tắt, không phải "để trống".' },
      { ten: 'elTimeDs', nhan: 'Thời gian trễ', donVi: '×0,1 s', min: 5, max: 300, goiY: 'Mặc định 10 = 1,0 giây' },
    ],
  },
  {
    ma: 'lat4', tieuDe: 'Ngưỡng bảo vệ', Icon: ShieldCheck, moTa: 'Quá áp · thấp áp · quá dòng. Bật lớp nào thì đặt ngưỡng cho lớp đó.',
    o: [
      { ten: 'ovSetV', nhan: 'Ngưỡng quá áp', donVi: 'V', min: 100, max: 300, goiY: 'Mặc định 270' },
      { ten: 'uvSetV', nhan: 'Ngưỡng thấp áp', donVi: 'V', min: 80, max: 250, goiY: 'Mặc định 170 — phải NHỎ HƠN ngưỡng quá áp' },
      { ten: 'ocSetA', nhan: 'Ngưỡng quá dòng', donVi: 'A', min: 1, max: 630, goiY: 'Mặc định 50' },
      { ten: 'ovUvTreS', nhan: 'Trễ trước khi cắt', donVi: 'giây', min: 0, max: 60, goiY: 'Số giây liên tục vượt ngưỡng mới cắt. 0 = dùng mặc định 5 giây.' },
      { ten: 'ovUvChoDongS', nhan: 'Chờ trước khi đóng lại', donVi: 'giây', min: 0, max: 240, goiY: 'Áp đã về dải rồi chờ ngần này mới đóng. 0 = dùng mặc định 60 giây.' },
    ],
  },
];

/*
 * ⛔ MỘT NGUỒN. Bảng bit này từng được chép lại ở đây; nay lấy từ `lib/baove.ts` — cùng chỗ
 *   với `datSoPha`/`datLop`, để không có bản sao thứ hai của sơ đồ bit `protectEn`.
 */
const LOP_BAO_VE = LOP_BAO_VE_BIT;

/* ------------------------------------------------------------------ màn hình */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-03` 11/09/2026 — BA LỆNH ĐỌC TÁCH RA THÀNH **MỘT HOOK**, và đây là thay đổi cấu
 *   trúc DUY NHẤT của lượt này. Không một lời gọi API, một mutation, một luật quyền nào đổi —
 *   các thân hàm dưới đây là **nguyên văn** cái đang chạy, chỉ đổi chỗ ở.
 *
 * 🔴 VÌ SAO BẮT BUỘC PHẢI TÁCH: mẫu lee duyệt đưa **giờ tủ** lên ô số thứ 4 ở ĐẦU trang, và
 *   đưa **ELR** + **lớp bảo vệ đang chạy** vào thẻ "Cần chú ý" ở giữa trang. Cả ba thứ ấy chỉ
 *   tới bằng ba lệnh hỏi thẳng xuống tủ (`/api/lenh/trangthai` · `/live` · `/doc`) — mà ba
 *   lệnh ấy đang nằm trong khối "Cài đặt tủ", tức là **cuối trang và mặc định THU LẠI**.
 *
 * ⛔ CÓ ĐÚNG HAI CÁCH, VÀ CÁCH KIA LÀ CÁI BẪY: hỏi tủ **lần thứ hai** từ đầu trang. Làm thế
 *   là dựng hai ảnh chụp của cùng một cái tủ, chụp ở hai thời điểm, rồi vẽ chúng cách nhau
 *   400 px trên cùng một màn hình. Ngày chúng lệch nhau — và chúng SẼ lệch, vì tủ sống — thì
 *   người trực đọc được hai sự thật và không có gì nói cho họ biết cái nào mới. Đây đúng họ
 *   với `soDo` đông lạnh và với "hai chỗ tự định nghĩa tủ gần nhất" mà `GD-02` vừa cắt.
 * ⇒ MỘT NGUỒN, NHIỀU CHỖ VẼ. Trang gọi hook một lần, rồi đưa cùng một `ct` cho mọi chỗ cần.
 *
 * ⚠ VÀ THU KHỐI LẠI **KHÔNG** LÀM GIẢM MỘT LỆNH NÀO xuống tủ: hook nằm ở trang, nó chạy dù
 *   khối đang thu. Đúng như hôm nay (khối vẫn mount), và **buộc phải thế** — ô giờ tủ ở đầu
 *   trang cần số. "Thu lại" ở đây là một việc của MẮT, không phải một việc của đường truyền.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export interface CaiDatTu {
  deviceId: string | null;
  online: boolean;
  dangDoc: boolean;
  dangGhi: boolean;
  loi: unknown;
  kq: KetQuaLenh | null;
  live: LiveState | null;
  gio: TrangThaiTu | null;
  sua: Record<string, string>;
  setSua: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  hoiLai: { patch: Patch; canhBao: string[] } | null;
  setHoiLai: (v: { patch: Patch; canhBao: string[] } | null) => void;
  dangDongBoGio: boolean;
  doc: () => Promise<void>;
  docLive: () => Promise<void>;
  docGio: () => Promise<void>;
  dongBoGio: () => Promise<void>;
  ghi: (p: Patch) => Promise<void>;
  /** Mốc MÁY CHỦ lúc đọc cấu hình gần nhất — cho dòng "lưu trên tủ · đọc lúc hh:mm". */
  docLucMs: number | null;
}

export function useCaiDatTu(deviceId: string | null, online: boolean): CaiDatTu {
  const [dangDoc, setDangDoc] = useState(false);
  const [dangGhi, setDangGhi] = useState(false);
  const [loi, setLoi] = useState<unknown>(null);
  const [kq, setKq] = useState<KetQuaLenh | null>(null);
  const [live, setLive] = useState<LiveState | null>(null);
  const [sua, setSua] = useState<Record<string, string>>({});
  const [hoiLai, setHoiLai] = useState<{ patch: Patch; canhBao: string[] } | null>(null);
  const [gio, setGio] = useState<TrangThaiTu | null>(null);
  const [dangDongBoGio, setDangDongBoGio] = useState(false);
  /*
   * ⛔ MỐC ĐỌC LÀ MỘT SỐ ĐO, KHÔNG PHẢI MỘT SUY ĐOÁN. Dòng "đọc lúc hh:mm" ở đầu khối phải
   *   nói ĐÚNG lúc khung cấu hình về tới, nếu không nó chỉ là một cái đồng hồ trang trí — và
   *   một mốc trang trí cạnh một bảng số là đúng thứ làm người ta tin số đã cũ là số mới.
   */
  const [docLucMs, setDocLucMs] = useState<number | null>(null);

  async function doc() {
    if (!deviceId) return;
    setDangDoc(true); setLoi(null);
    try {
      const r = await api.post<KetQuaLenh>('/api/lenh/doc', { deviceId });
      setKq(r); setSua({}); setDocLucMs(Date.now());
    } catch (e) { setLoi(e); } finally { setDangDoc(false); }
  }

  async function docLive() {
    if (!deviceId) return;
    try { setLive(await api.post<LiveState>('/api/lenh/live', { deviceId })); }
    catch (e) { setLoi(e); }
  }

  async function docGio() {
    if (!deviceId) return;
    try { setGio(await api.post<TrangThaiTu>('/api/lenh/trangthai', { deviceId })); }
    catch (e) { setLoi(e); }
  }

  /**
   * `lat2` — trên giao diện gọi là **"Đồng hồ tủ"**. `Date.now()` LÀ UTC, và hợp đồng chốt **đường truyền luôn là UTC**
   * (YC-14 mục A): app không bao giờ cộng sẵn 7 tiếng. `tzOffsetMin` đi CÙNG khung —
   * hai trường là một cặp, đặt riêng sẽ có một khoảng tủ giữ giờ mới + lệch cũ.
   */
  async function dongBoGio() {
    if (!deviceId) return;
    setDangDongBoGio(true); setLoi(null);
    try {
      await api.post('/api/lenh/gio', { deviceId, epochMs: Date.now(), tzOffsetMin: MUI_GIO_VN_PHUT });
      await new Promise((r) => setTimeout(r, 800));   // để tủ kịp ghi vào DS3231
      await docGio();
    } catch (e) { setLoi(e); } finally { setDangDongBoGio(false); }
  }

  async function ghi(p: Patch) {
    if (!deviceId) return;
    setHoiLai(null); setDangGhi(true); setLoi(null);
    try {
      const r = await api.post<KetQuaLenh>('/api/lenh/dat', { deviceId, patch: p });
      // Echo là TRẠNG THÁI — chỉ hiển thị, TUYỆT ĐỐI không gửi ngược lại.
      setKq(r); setSua({}); setDocLucMs(Date.now());
      void docLive();
    } catch (e) { setLoi(e); } finally { setDangGhi(false); }
  }

  useEffect(() => { if (deviceId && online) { doc(); docLive(); docGio(); } /* eslint-disable-next-line */ }, [deviceId, online]);

  return {
    deviceId, online, dangDoc, dangGhi, loi, kq, live, gio, sua, setSua,
    hoiLai, setHoiLai, dangDongBoGio, doc, docLive, docGio, dongBoGio, ghi, docLucMs,
  };
}

/* ------------------------------------------------- tóm tắt cho thẻ "Cần chú ý" */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-03` — HAI DÒNG CỦA THẺ "CẦN CHÚ Ý" DỰNG **Ở ĐÂY**, không dựng lại ở trang.
 *
 * 🔴 Mẫu lee duyệt gom ELR và "lớp bảo vệ đang chạy" lên thẻ "Cần chú ý" ở giữa trang, trong
 *   khi câu chữ đầy đủ của chúng vẫn ở lại khối "Cài đặt tủ". Đó là HAI CHỖ VẼ CÙNG MỘT KẾT
 *   LUẬN — và nếu mỗi chỗ tự đọc `elrOnline`/`protectEn` rồi tự kết luận thì sẽ có ngày một
 *   chỗ nói *"tủ nhìn thấy ELR"* còn chỗ kia nói ngược lại, **trên cùng một màn hình**. Đúng
 *   cái bệnh L67 mà dự án đã cắt bốn lần.
 * ⇒ Kết luận dựng MỘT LẦN, ở đây; trang chỉ vẽ nó ra.
 *
 * ⛔ VÀ BA CA, KHÔNG PHẢI HAI — Luật số 0 ở đúng chỗ dễ nuốt nhất:
 *     `live === null`      CHƯA ĐỌC            → xám, và nói là chưa đọc
 *     `elrOnline !== true` tủ KHÔNG nhìn thấy  → hổ phách
 *     `elrOnline === true` tủ nhìn thấy        → lá
 *   Gộp ca đầu vào ca giữa là biến *"tôi chưa hỏi"* thành *"tủ không thấy"* — một kết luận
 *   nặng, dựng từ chỗ không có dữ liệu.
 * ⚠ `=== true` chứ không `!!`: proto3 bỏ `false` khỏi dây, nên một phần sụn đời cũ **chưa có
 *   trường ấy** và một tủ khai `false` trông y hệt nhau. Cả hai đều KHÔNG phải "có".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export interface DongCanChuY {
  mau: 'tot' | 'canh' | 'chua-ro';
  cau: string;
  phu: string;
  /** GD-14 vòng 3 — tên trường / số hex. Trang vẽ trong "Chi tiết kỹ thuật" (chỉ Quản trị). */
  kyThuat?: string;
}

export function tomTatELR(ct: CaiDatTu): DongCanChuY {
  if (!ct.live) {
    return { mau: 'chua-ro', cau: 'Chống rò (ELR): chưa đọc',
      phu: 'Mở “Cài đặt tủ” rồi bấm “Đọc lại” để hỏi tủ một lần.' };
  }
  const s: any = ct.live.echo ?? {};
  if (s.elrOnline === true) {
    return { mau: 'tot', cau: 'Chống rò (ELR): tủ nhìn thấy',
      phu: `Rò ${Number(s.elrLeakMa ?? 0) || 0} mA · ngưỡng trong ELR ${Number(s.elrSetMa ?? 0) || 0} mA.` };
  }
  return { mau: 'canh', cau: 'Chống rò (ELR): tủ KHÔNG nhìn thấy',
    // 🔴 09/10/2026 — lee khoanh đỏ. Bỏ lối phủ định ("Không phải mất bảo vệ —"): nó trả lời
    //   một câu hỏi người đọc chưa kịp hỏi. Giữ nguyên sự thật kỹ thuật, nói thẳng.
    phu: 'ELR cắt bằng mạch riêng, không đi qua tủ.' };
}

export function tomTatLopBaoVe(ct: CaiDatTu): DongCanChuY {
  const goc: any = ct.kq?.echo ?? null;
  if (!goc) {
    return { mau: 'chua-ro', cau: 'Lớp bảo vệ đang chạy: chưa đọc cấu hình',
      phu: 'Mở “Cài đặt tủ” rồi bấm “Đọc lại từ tủ”.' };
  }
  const pe = Number(goc.protectEn ?? 0) || 0;
  const hex = `0x${pe.toString(16).padStart(2, '0').toUpperCase()}`;
  if (!khaiDuocLopTat()) {
    /*
     * ⛔ CÂU NÀY GIỮ NGUYÊN TỪNG CHỮ ý của khối cũ: công tắc là **cấu hình bạn đặt**, không
     *   phải trạng thái vũ trang thật. Đổi nó thành "N lớp đang tắt" là đúng cái YC-50 cấm —
     *   "0 lớp TẮT" người trực đọc thành "cả bốn lớp đang BẬT".
     */
    /*
     * ➕ GD-14 vòng 3 — mặt trước là câu người dùng; tên trường và số hex vào `kyThuat`.
     * ⚠ Câu nói "các lớp bảo vệ", KHÔNG chỉ "chống rò": nhánh này phủ cả bốn lớp
     *   (`LOP_BAO_VE_BIT`: dòng rò · quá áp · thấp áp · quá dòng).
     * ⚠ Nhánh này chạy THEO CẤU TẠO (`khaiDuocLopTat()` là hằng), KHÔNG theo việc tủ có gửi
     *   `protectEnEffective` hay không. Nên "Tủ chưa báo…" CHỈ in khi khung thật sự vắng
     *   trường ấy; tủ đã gửi mà app chưa dùng để kết luận thì nói đúng câu ấy.
     */
    const coEff = Object.prototype.hasOwnProperty.call(goc, 'protectEnEffective');
    return { mau: 'canh', cau: 'Lớp bảo vệ đang chạy: chưa kết luận',
      /*
       * 🔴 09/10/2026 — lee khoanh đỏ. Câu cũ dài 24 từ và mở đầu bằng "Chưa kết luận được…",
       *   tức app đang kể lại việc NÓ không làm được. Đảo lại thành việc NGƯỜI DÙNG cần biết:
       *   công tắc dưới đây là cấu hình, chưa phải trạng thái thật. Bốn tên lớp bảo vệ chuyển
       *   xuống "Chi tiết kỹ thuật" cùng chỗ với `protectEn`.
       */
      phu: coEff
        ? 'Công tắc là cấu hình bạn đặt. Tủ chưa báo lớp nào đang chạy thật.'
        : 'Công tắc là cấu hình bạn đặt. Tủ chưa báo trạng thái bảo vệ.',
      kyThuat: [
        coEff ? `protectEnEffective = ${Number(goc.protectEnEffective) || 0} (app chưa dùng để kết luận lớp)`
          : 'protectEnEffective: không có trong khung cấu hình tủ gửi',
        `protectEn (cấu hình đặt) = ${pe} (${hex})`,
      ].join(' · ') };
  }
  const nguong: Record<string, number> = {};
  for (const l of LOP_BAO_VE_BIT) nguong[l.truongNguong] = Number(goc[l.truongNguong] ?? 0) || 0;
  const dsTat = lopDangTat(pe, nguong);
  if (dsTat.length === 0) {
    return { mau: 'tot', cau: 'Lớp bảo vệ đang chạy: đủ lớp', phu: 'Theo cấu hình bạn đặt.',
      kyThuat: `protectEn (cấu hình đặt) = ${pe} (${hex})` };
  }
  return { mau: 'canh', cau: `Lớp bảo vệ đang chạy: ${dsTat.length} lớp TẮT`,
    phu: `${dsTat.map((l) => l.ten.toLowerCase()).join(' · ')} — tủ vẫn chạy lịch, nhưng không cắt khi vượt ngưỡng.` };
}

/* ------------------------------------------------------------------ màn hình */

/**
 * BA THẺ — và **mỗi thẻ khai luôn những trường nó chứa**.
 *
 * 🔴 Vì sao danh sách trường nằm ở đây chứ không đếm bằng mắt: tab đang ẩn vẫn có thể đang
 *   giữ ô người dùng đã sửa, và cái chấm đếm trên mỗi tab là thứ DUY NHẤT nói ra điều đó.
 *   Muốn chấm ấy đúng thì phải biết trường nào thuộc thẻ nào — bằng dữ liệu, không bằng chỗ
 *   đứng trong JSX. Một `<HangO>` đổi chỗ giữa hai thẻ mà bảng này không đổi theo thì chấm
 *   đếm sai, **và nó sai một cách im lặng**.
 * ⚠ `protectEn` thuộc thẻ NGƯỠNG BẢO VỆ, dù ô "Số pha của tủ" ở thẻ THIẾT BỊ ĐO cũng ghi vào
 *   nó (số pha sống trong bit 6-7 của chính `protectEn`). Một trường, hai chỗ sửa — nên nó
 *   chỉ được đếm MỘT lần, và đếm ở chỗ nó mang tên.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-10` việc 1 — `HangO` LÊN CẤP MODULE, và đây là một lỗi ĐÁNG HỌC chứ không phải một
 * chỗ xộc xệch.
 *
 * 🔴 TRIỆU CHỨNG dev_device đo 14/09: ba trong bốn ô `<select>` của khối cài đặt **sổ ra là
 *   đóng ngay**. Không phải "khó bấm" — bấm xuống thì danh sách mở, và biến mất trước khi
 *   kịp chọn.
 * ⛔ GỐC KHÔNG NẰM Ở `<select>`. `HangO` được khai bằng `const HangO = ({ten}) => …` **bên
 *   trong thân hàm vẽ**, nên MỖI LẦN VẼ LẠI nó là một **hàm mới** — tức một **kiểu component
 *   mới** với React. React so kiểu bằng đồng nhất thức (`===`); kiểu khác ⇒ nó **tháo cả cây
 *   con và gắn lại**, chứ không cập nhật. Nút `<select>` bị gỡ khỏi DOM và dựng lại; danh
 *   sách đang sổ của một phần tử đã chết thì không còn gì để sổ.
 * ⚠ VÌ SAO CHỈ 3/4 Ô HỎNG: ô thứ tư (`Số pha của tủ`) không đi qua `HangO`. Đúng cái làm nó
 *   khó thấy — "ô này được, ô kia không" đọc ra như lỗi của từng ô, và người đi sửa sẽ đi
 *   xem xét từng `<select>`, ở một chỗ không hỏng.
 * ⚠ VÀ NÓ VẼ LẠI RẤT NHIỀU: mỗi ký tự gõ vào bất kỳ ô nào cũng đổi `sua` ⇒ vẽ lại cả khối ⇒
 *   tháo/gắn lại toàn bộ. Mất luôn cả con trỏ trong ô nhập, không riêng `<select>`.
 *
 * ⇒ LUẬT MỚI, và nó có phép canh (`[113]a`): **KHÔNG khai component bên trong thân vẽ.** Không
 *   phải "nhớ dùng `useCallback`" — `useCallback` không cứu được, vì phụ thuộc của nó chứa
 *   `sua`, thứ đổi mỗi phím gõ, nên đồng nhất thức vẫn đổi. Lối ra duy nhất là ĐƯA RA NGOÀI.
 * ⚠ Giá phải trả: những thứ trước đây khép kín nay thành THAM SỐ. Gom vào một `bc` (bối cảnh)
 *   để chỗ gọi chỉ thêm một chữ — và `bc` là một vật mới mỗi lần vẽ thì KHÔNG sao: nó làm
 *   `HangO` **vẽ lại**, không làm nó **gắn lại**. Hai thứ khác nhau, và chỉ thứ sau mới giết
 *   cái `<select>`.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-10` việc 3 — MÚI GIỜ VIỆT NAM, một cái TÊN thay cho một con số trần.
 *
 * 🔴 `420` xuất hiện trần trong lời gọi `POST /api/lenh/gio` và **lại một lần nữa** trong câu
 *   chữ vẽ ra màn hình (`tz === 420 ? ' (Việt Nam)' : ''`). Hai chỗ, một sự thật — và không
 *   có gì buộc chúng bằng nhau. Cùng một họ với `OSoLon` hai bản và `quyetDinhCoTuyen` hai
 *   bản: mỗi lần lặp một hằng số là một chỗ để trôi.
 * ⚠ Và `420` trần thì **không đọc được**: người đọc mã phải tự chia 60 mới biết nó là +7 giờ.
 *   Một cái tên trả lời ngay, và nó là thứ `grep` tìm ra được khi có ngày phải đổi.
 * ⛔ KHÔNG suy múi giờ từ trình duyệt (`getTimezoneOffset`): máy của người trực có thể đặt sai,
 *   và giờ tủ là một thứ đi xuống thiết bị ngoài hiện trường — nó phải đến từ một hằng số
 *   khai rõ, không từ cấu hình máy của người đang ngồi trước màn hình.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export const MUI_GIO_VN_PHUT = 420;   // +07:00

/**
 * Lệch múi giờ (PHÚT) → `GMT+07:00`. Công thức CHUNG, không phải một bảng tra.
 *
 * 🔴 Vì sao chung: tủ có thể báo bất kỳ số nào — kể cả một số âm, kể cả một múi giờ lẻ 30/45
 *   phút (Ấn Độ +05:30, Nepal +05:45, Chatham +12:45). Một bảng `if (tz === 420) … else`
 *   in ra "420 phút" cho mọi múi giờ khác, và đó đúng là thứ người đọc không dịch nổi.
 * ⚠ Dấu lấy theo `tz`, còn GIỜ/PHÚT lấy trên TRỊ TUYỆT ĐỐI: `-330` phải ra `GMT-05:30`, không
 *   phải `GMT-5:-30`. Đây là chỗ duy nhất của phép này có cửa để sai.
 */
export function cauMuiGio(tz: number): string {
  const dau = tz < 0 ? '-' : '+';
  const tuyetDoi = Math.abs(tz);
  const gio = Math.floor(tuyetDoi / 60);
  const phut = tuyetDoi % 60;
  return `GMT${dau}${String(gio).padStart(2, '0')}:${String(phut).padStart(2, '0')}`;
}

interface BoiCanhO {
  goc: Record<string, number>;
  sua: Record<string, string>;
  setSua: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  lech: KetQuaLenh['lech'];
  khoa: boolean;
}

/** Tra một ô theo tên. `LAT` ở cấp module nên phép tra cũng ở cấp module — cùng một lý do. */
const oCua = (ten: string) => LAT.flatMap((l) => l.o).find((o) => o.ten === ten)!;

function HangO({ ten, bc }: { ten: string; bc: BoiCanhO }) {
  return (
    <ONhap
      o={oCua(ten)} giaTriGoc={bc.goc[ten]} giaTriSua={bc.sua[ten]}
      doiSang={(v) => bc.setSua((st) => ({ ...st, [ten]: v }))}
      xoaSua={() => bc.setSua(({ [ten]: _, ...r }) => r)}
      biTuChoi={bc.lech.find((l) => l.truong === ten)}
      khoa={bc.khoa}
    />
  );
}

const THE_CAI_DAT = [
  { ma: 'do', ten: 'Thiết bị đo',
    truong: ['modbusModel', 'modbusSlaveAdd', 'elrModel', 'elrSlaveAdd', 'modbusBaudrate', 'modbusParity'] },
  { ma: 'ro', ten: 'Chống rò điện', truong: ['elSetMa', 'elTimeDs'] },
  { ma: 'bao-ve', ten: 'Ngưỡng bảo vệ',
    truong: ['protectEn', 'ovSetV', 'uvSetV', 'ocSetA', 'ovUvTreS', 'ovUvChoDongS'] },
] as const;

type MaThe = (typeof THE_CAI_DAT)[number]['ma'];

export default function CaiDatThietBi({
  ct, coTheGhi, mo, onDoiMo,
}: {
  ct: CaiDatTu;
  coTheGhi: boolean;
  /** Khối đang mở hay thu. **Trang** giữ trạng thái này (nút ở đầu trang cũng mở được nó). */
  mo: boolean;
  onDoiMo: () => void;
}) {
  const {
    deviceId, online, dangDoc, dangGhi, loi, kq, live, gio, sua, setSua,
    hoiLai, setHoiLai, dangDongBoGio, doc, docLive, docGio, dongBoGio, ghi, docLucMs,
  } = ct;
  const goc = kq?.echo ?? null;
  const [the, setThe] = useState<MaThe>('do');

  /* --------- chỉ những trường THẬT SỰ đổi mới được vào gói --------- */
  const patch = useMemo<Patch>(() => {
    const p: Patch = {};
    for (const [k, v] of Object.entries(sua)) {
      const n = Number(String(v).trim());
      if (!Number.isInteger(n) || n < 0) continue;
      if (goc && Object.prototype.hasOwnProperty.call(goc, k) && goc[k] === n) continue; // không đổi ⇒ không gửi
      p[k] = n;
    }
    return p;
  }, [sua, goc]);

  const tenSua = Object.keys(patch);

  /* --------- ba chốt chặn ở màn hình (khác CLI: CLI cố ý không chặn) --------- */
  const chan = useMemo<string[]>(() => {
    const r: string[] = [];
    const lay = (t: string) => (Object.prototype.hasOwnProperty.call(patch, t) ? patch[t] : goc?.[t]);

    for (const lat of LAT) for (const o of lat.o) {
      if (!Object.prototype.hasOwnProperty.call(patch, o.ten)) continue;
      const v = patch[o.ten];
      if (o.chon && !o.chon.some((c) => c.gt === v)) r.push(`${o.nhan}: giá trị ${v} không có trong danh sách.`);
      if (o.min !== undefined && o.max !== undefined && v !== 0 && (v < o.min || v > o.max)) {
        r.push(`${o.nhan}: ${v}${o.donVi ? ' ' + o.donVi : ''} nằm ngoài dải ${o.min}–${o.max}.`);
      }
    }

    const ov = lay('ovSetV'), uv = lay('uvSetV');
    if (typeof ov === 'number' && typeof uv === 'number' && ov > 0 && uv > 0 && uv >= ov) {
      r.push(`Ngưỡng thấp áp (${uv} V) phải NHỎ HƠN ngưỡng quá áp (${ov} V).`);
    }

    const pe = lay('protectEn');
    if (typeof pe === 'number') {
      for (const l of LOP_BAO_VE) {
        if (((pe >>> l.bit) & 1) !== 1) continue;
        const ng = lay(l.truongNguong);
        if (!ng) r.push(`Đã bật bảo vệ "${l.ten}" nhưng ngưỡng của nó đang là 0 — tủ sẽ coi lớp đó CHƯA BẬT và không cắt.`);
      }
    }
    return r;
  }, [patch, goc]);

  function batDauGhi() {
    if (!tenSua.length || chan.length) return;
    const canhBao: string[] = [];
    for (const lat of LAT) for (const o of lat.o) {
      if (o.canhBaoSo0 && patch[o.ten] === 0) canhBao.push(o.canhBaoSo0);
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'protectEn') && patch.protectEn === 0) {
      canhBao.push('Đặt "Bảo vệ" = 0 nghĩa là TẮT TOÀN BỘ bảo vệ: dòng rò, quá áp, thấp áp, quá dòng.');
    }
    if (canhBao.length) setHoiLai({ patch, canhBao });
    else void ghi(patch);
  }

  if (!deviceId) {
    return <Card title="Cài đặt tủ"><p className="text-sm text-chu-2">Tủ chưa đăng ký nên chưa có deviceId.</p></Card>;
  }

  return (
    <div id="khoi-cai-dat" className="overflow-hidden rounded-the border border-vien bg-nen-1">
      {/*
        * ═══════════════════════════════════════════════════════════════════════════════
        * ➕ `GD-03` — ĐẦU KHỐI THU-MỞ. Nguyên tắc lee chốt qua mẫu: *thứ nhìn mỗi ngày lên
        *   trên và to; thứ CÀI MỘT LẦN xuống dưới và THU LẠI.*
        *
        * 🔴 CẢ HÀNG LÀ NÚT BẤM (`<button>`), không phải mỗi cái mũi tên. Một mũi tên 12 px
        *   là một đích bấm 12 px; người trực ngoài hiện trường bấm bằng ngón cái. Và một
        *   tiêu đề bấm được mà **trông** như chữ thường thì không ai bấm — nên có `mũi tên`
        *   xoay + `cursor-pointer` + `aria-expanded` để cả mắt lẫn trình đọc màn hình biết.
        * ⚠ Nút "Đọc lại từ tủ" nằm TRONG hàng ấy nhưng phải `stopPropagation()`: không có nó
        *   thì mỗi lần đọc lại là một lần khối tự đóng/mở — đúng kiểu lỗi làm người ta mất
        *   chỗ đang xem.
        * ═══════════════════════════════════════════════════════════════════════════════
        */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={onDoiMo}
          aria-expanded={mo}
          aria-controls="than-cai-dat"
          className="flex flex-1 items-center gap-2 whitespace-nowrap text-left"
        >
          <ChevronDown className={`h-4 w-4 shrink-0 text-chu-3 transition-transform ${mo ? '' : '-rotate-90'}`} />
          {/*
            * 🔴 09/10/2026 — lee khoanh đỏ. Câu "chỉ những ô bạn sửa mới được gửi xuống tủ" nằm
            *   cạnh tiêu đề khi khối còn ĐÓNG, tức nó giải thích một việc chưa ai định làm.
            *   Chuyển vào `title`; khi mở khối ra, nút Lưu đã nói đủ bằng số ô đang đổi.
            */}
          <b className="text-sm text-chu-1" title="Chỉ những ô bạn sửa mới được gửi xuống tủ">Cài đặt tủ</b>
        </button>
        {kq?.cachGhep === 'tu-khoi-xuong' && (
          <Badge tone="amber">Tủ trả khung không mang số yêu cầu</Badge>
        )}
        {/*
          * ⛔ "lưu trên tủ · đọc lúc hh:mm" — MỐC THẬT, không phải chữ trang trí. Chưa đọc lần
          *   nào thì nói *"chưa đọc"*, không vẽ một giờ nào cả (Luật số 0).
          */}
        <span className="text-xs text-chu-3">
          lưu trên tủ · {docLucMs ? `đọc lúc ${fmtTime(docLucMs)}` : 'chưa đọc lần nào'}
        </span>
        <Button
          variant="secondary"
          onClick={(e?: any) => { e?.stopPropagation?.(); void doc(); void docLive(); }}
          loading={dangDoc}
          disabled={!online}
        >
          Đọc lại từ tủ
        </Button>
      </div>

      {mo && (
      <div id="than-cai-dat" className="border-t border-vien px-4 py-4">
      {!online && <p className="mb-3 text-sm text-canh">Tủ đang offline — chỉ xem được số đọc lần cuối, không gửi được lệnh.</p>}
      {loi ? <ErrorBox error={loi} onRetry={() => void doc()} /> : null}
      {dangDoc && !goc ? <Spinner label="Đang đọc cấu hình từ tủ…" /> : null}

      {goc && (
        <>
          {kq!.thieu.length > 0 && (
            <p className="mb-3 rounded bg-canh-nen p-2 text-sm text-canh">
              ⚠ Tủ chỉ trả về {14 - kq!.thieu.length}/14 trường. Thiếu: {kq!.thieu.join(', ')}.
              Theo hợp đồng, tủ phải echo đủ 14 trường — số hiển thị bên dưới có thể không đầy đủ.
            </p>
          )}

          {(() => {
            /*
             * ─────────────────────────────────────────────────────────────────────────
             * BỐ CỤC theo bản mẫu `mau_cai_dat.html` (đặc tả là BẢN MẪU, không phải văn xuôi).
             *   · nhãn TRÁI · ô nhập PHẢI — mắt dò một cột nhãn, không dò zig-zag
             *   · chú giải vào TOOLTIP `?` — nó không được chiếm một dòng của mỗi hàng
             *   · HAI CỘT ở màn rộng, MỘT CỘT ở màn hẹp
             *   · công tắc lớp bảo vệ và ngưỡng của nó NẰM CÙNG MỘT HÀNG
             * ─────────────────────────────────────────────────────────────────────────
             */
            const pe = Number(sua.protectEn ?? goc.protectEn ?? 0);
            const nguongHienTai: Record<string, number> = {};
            for (const l of LOP_BAO_VE) {
              nguongHienTai[l.truongNguong] = Number(sua[l.truongNguong] ?? goc[l.truongNguong] ?? 0);
            }
            const dsTat = lopDangTat(pe, nguongHienTai);
            const quenBat = dsTat.filter((l) => l.lyDo === 'nguoi-da-dat-nguong');
            const khoa = !coTheGhi || !online;
            const datPe = (v: number) => setSua((st) => ({ ...st, protectEn: String(v) }));
            /*
             * ➕ `GD-10` — BỐI CẢNH cho `HangO`, thay cho phần khép kín cũ. Vật này mới mỗi lần
             *   vẽ, và **cố ý không memo hoá**: nó chỉ cần làm `HangO` vẽ lại. Thứ tuyệt đối
             *   không được mới là **kiểu** của `HangO` — nay ở cấp module nên nó cố định.
             */
            const bc: BoiCanhO = { goc, sua, setSua, lech: kq!.lech, khoa };

            return (
              <>
              {/*
                * ═══════════════════════════════════════════════════════════════════════
                * ➕ `GD-03` — BA THẺ THÀNH BA **TAB**, mỗi lúc chỉ một thẻ hiện.
                *
                * Bản cũ xếp thẻ 1 + thẻ 2 hai cột, thẻ 3 ("Ngưỡng bảo vệ") chiếm cả hai cột
                * bên dưới — tức là cả ba luôn hiện, và khối cài đặt dài hơn cả phần vận
                * hành phía trên nó. Mẫu lee duyệt đảo lại: cài đặt là thứ **đụng một lần**,
                * nên nó thu lại, và trong lúc mở thì chỉ hiện đúng thẻ đang xem.
                *
                * ⛔ VÀ ĐÂY LÀ CHỖ DỄ LÀM HỎNG NHẤT CỦA CẢ LƯỢT: **ẩn một thẻ KHÔNG được bỏ
                *   những ô sửa trong thẻ ấy.** `sua` là state của cả khối, không phải của
                *   từng tab, nên chuyển tab không đụng tới nó — người dùng sửa một ngưỡng ở
                *   tab 3, nhảy sang tab 1 xem địa chỉ công tơ, quay lại: ô vẫn còn sửa, và
                *   thanh chân vẫn đếm đủ. Nếu `sua` bị gắn vào tab thì đổi tab = mất việc,
                *   và mất **trong im lặng**.
                * ⚠ Vì thế thanh chân ở dưới đếm trên `tenSua` của CẢ khối, và nó nói ra thẻ
                *   nào đang chứa ô sửa — người bấm Gửi phải biết mình sắp gửi cả thứ ở thẻ
                *   họ không nhìn thấy.
                * ═══════════════════════════════════════════════════════════════════════
                */}
              <div className="mb-3 flex flex-wrap gap-1.5">
                {THE_CAI_DAT.map((t) => {
                  const nSua = t.truong.filter((x) => tenSua.includes(x)).length;
                  return (
                    <button
                      key={t.ma}
                      type="button"
                      onClick={() => setThe(t.ma)}
                      aria-pressed={the === t.ma}
                      className={`rounded-thuoc px-3 py-1.5 text-xs font-medium transition ${
                        the === t.ma ? 'bg-nhan text-chu-1' : 'bg-nen-2 text-chu-2 hover:text-chu-1'}`}
                    >
                      {t.ten}
                      {/*
                        * 🔴 CHẤM TRÊN TAB KHÔNG PHẢI TRANG TRÍ — nó là câu trả lời cho *"tôi
                        *   còn sửa gì ở thẻ tôi không nhìn thấy?"*. Không có nó thì ba tab
                        *   biến một danh sách đang thấy thành hai phần ba bị giấu.
                        */}
                      {nSua > 0 && (
                        <span className="ml-1.5 rounded-thuoc bg-canh-nen px-1.5 text-xs font-semibold text-canh">
                          {nSua}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="grid items-start gap-3">

                {/* ══ thẻ 1 · THIẾT BỊ ĐO ══ */}
                {the === 'do' && (
                <TheCaiDat tieuDe="Thiết bị đo" Icon={Gauge} phu="RS-485 · dùng chung">
                  <HangO ten="modbusModel" bc={bc} />
                  <HangO ten="modbusSlaveAdd" bc={bc} />
                  <SoPhaCuaTu pe={pe} khoa={khoa} doiSang={datPe} />
                  <HangO ten="elrModel" bc={bc} />
                  <HangO ten="elrSlaveAdd" bc={bc} />
                  <HangO ten="modbusBaudrate" bc={bc} />
                  <HangO ten="modbusParity" bc={bc} />
                </TheCaiDat>
                )}

                {/* ══ thẻ 2 · CHỐNG RÒ ══ */}
                {the === 'ro' && (
                <TheCaiDat tieuDe="Chống rò điện" Icon={Zap}>
                  <HangLop
                    lop={LOP_BAO_VE[0]} pe={pe} khoa={khoa} doiSangPe={datPe}
                    o={oCua('elSetMa')} giaTriGoc={goc.elSetMa} giaTriSua={sua.elSetMa}
                    doiSang={(v) => setSua((st) => ({ ...st, elSetMa: v }))}
                    biTuChoi={kq!.lech.find((l) => l.truong === 'elSetMa')}
                  />
                  <HangO ten="elTimeDs" bc={bc} />
                  <p className="border-t border-vien px-3 py-2 text-xs leading-relaxed text-chu-3">
                    ELR đo và tự cắt bằng mạch riêng. Hai ô này chỉ nói cho tủ biết ELR đang đặt gì —
                    tắt ở đây <b className="text-chu-2">không</b> làm ELR ngừng bảo vệ.
                    {' '}Lớp này <b className="text-chu-2">không có công tắc</b>: tủ vũ trang chống rò
                    theo <code>elrModel</code> và <code>elSetMa</code>, không theo{' '}
                    <code>protectEn</code>. Trang này chưa nói được nó đang bật hay tắt — sẽ nói được
                    khi đọc được trạng thái bảo vệ thật từ tủ.
                  </p>
                  {/* GD-14 vòng 3 — tên trường rời câu mặt trước. */}
                  <div className="px-3 pb-2"><ChiTietKyThuat><pre className="whitespace-pre-wrap font-mono text-xs">{`protectEnEffective — chưa dùng để kết luận lớp (khaiDuocLopTat() = false)`}</pre></ChiTietKyThuat></div>
                  {/*
                    * 🔴 DẤU VẾT BIT CHẾT — chỉ hiện khi tủ THẬT SỰ đang lưu nó (YC-50 vòng 5).
                    *   Firmware cố ý không cắt bit chết khi ghi, để giữ dấu vết kẻ đã đặt. Nên
                    *   nếu nó bật, có người đã gạt một ô không có tác dụng — nhiều khả năng ở
                    *   MÀN HÌNH TRÊN TỦ (`main.cpp:1294` cũng ghi `protect_en`).
                    * ⛔ Câu này nói về một SỐ ĐO nhận được, KHÔNG khẳng định ai đặt (YC-47).
                    */}
                  {bitChetDangBat(pe).length > 0 && (
                    <div className="flex items-start gap-2 border-t border-canh/40 bg-canh-nen px-3 py-2 text-sm text-canh">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-canh" />
                      <span>
                        <b className="text-chu-1">Tủ đang lưu một ô không có tác dụng</b> — bit{' '}
                        {bitChetDangBat(pe).join(', ')} của <code>protectEn</code>. Tủ{' '}
                        <b className="text-chu-2">không đọc</b> bit này, nên nó không bật hay tắt gì
                        cả. Nếu ai đó vừa gạt một ô &quot;dòng rò&quot; trên màn hình tủ, đó là ô ấy.
                        {' '}Máy chủ để nguyên, không xoá hộ.
                      </span>
                    </div>
                  )}
                </TheCaiDat>
                )}

                {/* ══ thẻ 3 · NGƯỠNG BẢO VỆ — nay là một TAB riêng, không còn chiếm hai cột ══ */}
                {the === 'bao-ve' && (
                <div>
                  <TheCaiDat
                    tieuDe="Ngưỡng bảo vệ" Icon={ShieldCheck}
                    /*
                     * ⛔ KHÔNG in "N lớp TẮT" khi app không kết luận được lớp nào — "0 lớp TẮT"
                     *   người trực đọc thành "cả bốn lớp đang BẬT", đúng chiều nguy của YC-50.
                     *   Câu này hỏi `khaiDuocLopTat()`, KHÔNG hỏi `dsTat.length`.
                     */
                    phu={`protectEn = ${pe} (0x${pe.toString(16).padStart(2, '0').toUpperCase()}) · ${
                      khaiDuocLopTat() ? `${dsTat.length} lớp TẮT` : 'chưa kết luận được lớp nào TẮT'
                    } · ${NHAN_SO_PHA[docSoPha(pe)].toLowerCase()}`}
                  >
                    {LOP_BAO_VE.slice(1).map((l) => (
                      <HangLop
                        key={l.bit} lop={l} pe={pe} khoa={khoa} doiSangPe={datPe}
                        o={oCua(l.truongNguong)}
                        giaTriGoc={goc[l.truongNguong]} giaTriSua={sua[l.truongNguong]}
                        doiSang={(v) => setSua((st) => ({ ...st, [l.truongNguong]: v }))}
                        biTuChoi={kq!.lech.find((x) => x.truong === l.truongNguong)}
                      />
                    ))}
                    <HangO ten="ovUvTreS" bc={bc} />
                    <HangO ten="ovUvChoDongS" bc={bc} />

                    {/*
                      * 🔴 HAI DẢI, TÁCH HẲN — chốt với dev_device 04/09.
                      *   Bản mẫu để MỘT dải vàng làm cả hai việc, và vì thế màu vàng mất nghĩa.
                      *   · NÊU SỰ THẬT: trung tính, luôn hiện khi còn lớp tắt. Trang này người
                      *     ta CHỦ ĐỘNG mở ra để đọc — luật "một dải luôn hiện thì mắt thôi
                      *     nhìn nó" là luật về chú ý bị LẤY, không áp ở đây.
                      *   · VÀNG: chỉ khi có DẤU HIỆU QUÊN BẬT — ngưỡng KHÁC MẶC ĐỊNH mà lớp
                      *     vẫn tắt. Ngưỡng xuất xưởng là 270/170/50 (KHÁC 0), nên luật
                      *     "ngưỡng khác 0" sẽ vàng ở mọi tủ mới — đo ở `[51]`.
                      */}
                    {!khaiDuocLopTat() && (
                      <div className="flex items-start gap-2 border-t border-vien px-3 py-2 text-sm text-chu-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-chu-3" />
                        <span>
                          <b className="text-chu-1">Chưa kết luận được lớp nào đang TẮT.</b>
                          {' '}Công tắc bên trên là <b className="text-chu-2">cấu hình bạn đặt</b>,
                          không phải trạng thái vũ trang thật: tủ tự bật thêm lớp khi có gắn công tơ,
                          và tự gỡ lớp khi ngưỡng nằm ngoài dải cho phép.
                          {' '}Trang này sẽ nói được khi đọc được trạng thái bảo vệ thật từ tủ.
                          <ChiTietKyThuat><pre className="whitespace-pre-wrap font-mono text-xs">{`protectEnEffective — chưa dùng để kết luận lớp (khaiDuocLopTat() = false)`}</pre></ChiTietKyThuat>
                        </span>
                      </div>
                    )}
                    {khaiDuocLopTat() && dsTat.length > 0 && (
                      <div className="flex items-start gap-2 border-t border-vien px-3 py-2 text-sm text-chu-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-chu-3" />
                        <span>
                          <b className="text-chu-1">{dsTat.length} lớp bảo vệ đang TẮT</b>
                          {' — '}{dsTat.map((l) => l.ten.toLowerCase()).join(' · ')}.
                          {' '}Tủ vẫn chạy lịch, nhưng không cắt khi vượt ngưỡng.
                        </span>
                      </div>
                    )}
                    {coDauHieuQuenBat(dsTat) && (
                      <div className="flex items-start gap-2 border-t border-canh/40 bg-canh-nen px-3 py-2 text-sm text-canh">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-canh" />
                        <span>
                          <b className="text-chu-1">Có thể đã quên bật</b> — ngưỡng của{' '}
                          {quenBat.map((l) => l.ten.toLowerCase()).join(' · ')} đã được sửa khác mặc định,
                          nhưng lớp vẫn TẮT. Bật công tắc bên trái dòng tương ứng.
                        </span>
                      </div>
                    )}
                  </TheCaiDat>
                </div>
                )}
              </div>
              </>
            );
          })()}

          {chan.length > 0 && (
            <ul className="mb-3 mt-3 space-y-1 rounded bg-nguy-nen p-3 text-sm text-nguy">
              {chan.map((c, i) => <li key={i}>✗ {c}</li>)}
            </ul>
          )}

          {/*
            * ⚠ THANH GỬI DÍNH ĐÁY, không nằm ở cuối trang cuộn: người ta sửa một ô ở nửa
            *   trên rồi phải cuộn xuống mới thấy nút — và trên đường cuộn thì quên mất mình
            *   vừa sửa gì. `sticky` chứ không `fixed`: nó thuộc về thẻ này, không phải cửa sổ.
            *
            * 🔴 `bottom-7`, KHÔNG phải `bottom-0` — và con số ấy do ẢNH CHỤP dạy, không phải
            *   do tôi tính: `bottom-0` thì thanh trạng thái toàn cục (`fixed bottom-0 h-7`)
            *   nằm ĐÈ lên đúng cái nút Gửi. Nguồn "trông đúng"; chỉ ảnh mới thấy (L19).
            *
            * ═══════════════════════════════════════════════════════════════════════════
            * ➕ `GD-03` — THANH NÀY **CHỈ HIỆN KHI CÓ Ô ĐÃ SỬA**, và đó là một thay đổi có
            *   lý do chứ không phải dọn cho gọn.
            *
            * Bản cũ luôn hiện, với một nút Gửi xám và câu *"Chưa sửa ô nào"*. Một thanh dính
            * đáy luôn có mặt thì mắt **thôi nhìn nó** — và ngày nó có việc để nói (bạn đang
            * cầm 2 ô chưa gửi) thì nó đã bị xếp vào loại đồ trang trí. Đúng luật L52 mà dự
            * án đã cắt một lần với dải cảnh báo mức 1.
            * ⇒ Không sửa gì ⇒ không có thanh. Có sửa ⇒ thanh hiện, **nói số ô**, và nói tên
            *   từng ô (người bấm Gửi phải biết mình đang gửi cả những ô ở tab đang bị giấu).
            * ⚠ Vế `chan.length > 0` GIỮ NGUYÊN trong `disabled` — một ô sai dải thì thanh vẫn
            *   hiện (có sửa mà), nút vẫn khoá, và danh sách ✗ ở ngay trên nói vì sao.
            * ═══════════════════════════════════════════════════════════════════════════
            */}
          {tenSua.length > 0 && (
            <div className={`sticky ${DAY_THANH_DUOI} z-10 -mx-4 mt-3 flex flex-wrap items-center gap-3 border-t border-canh/40 bg-nen-1 px-4 py-2`}>
              <span className="text-sm font-medium text-canh">
                Bạn đã sửa <b>{tenSua.length}</b> ô — chưa gửi.
              </span>
              <span className="min-w-0 flex-1 truncate text-xs text-chu-2" title={tenSua.join(' · ')}>
                {tenSua.join(' · ')}
              </span>
              <Button variant="ghost" onClick={() => setSua({})}>Bỏ sửa</Button>
              <Button onClick={batDauGhi} loading={dangGhi} disabled={!coTheGhi || !online || chan.length > 0}>
                Gửi {tenSua.length} thay đổi xuống tủ
              </Button>
            </div>
          )}

          {kq!.lech.length > 0 && (
            <div className="mt-3 rounded bg-canh-nen p-3 text-sm text-canh">
              <b>Tủ từ chối {kq!.lech.length} trường</b> (giá trị ngoài dải thì tủ giữ số cũ, không kẹp về biên):
              <ul className="mt-1 space-y-0.5">
                {kq!.lech.map((l) => <li key={l.truong}>· {l.truong}: xin <b>{l.xin}</b>, tủ giữ <b>{l.thucTe}</b></li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {/*
        * ⚠ HAI KHỐI NÀY Ở LẠI TRONG "Cài đặt tủ", KHÔNG lên đầu trang — và tôi nói ra vì nó
        *   trông giống một chỗ bỏ sót:
        *     · `Lat2Gio` giữ **ô đồng bộ giờ + câu giải thích UTC/múi giờ** — một việc CÀI ĐẶT.
        *       Ô số "Đồng hồ tủ" ở đầu trang chỉ ĐỌC cùng `gio` ấy, không hỏi tủ lần thứ hai.
        *     · `TrangThaiSong` là **ảnh chụp sống đủ 5 mục** (điện áp · ba ngõ ra · cổng an
        *       toàn · ELR · tuổi khung). Thẻ "Cần chú ý" ở trên chỉ rút ra ĐÚNG dòng ELR.
        *   Chép chúng lên đầu trang là dựng bản sao thứ hai của cùng một ảnh chụp.
        */}
      <Lat2Gio
        gio={gio} online={online} coTheGhi={coTheGhi}
        dangDongBo={dangDongBoGio}
        onDoc={() => void docGio()} onDongBo={() => void dongBoGio()}
      />

      <TrangThaiSong live={live} onDoc={() => void docLive()} online={online} />
      </div>
      )}

      <ConfirmDialog
        open={!!hoiLai}
        title="Xác nhận việc này trước khi gửi"
        danger
        confirmText="Tôi hiểu, vẫn gửi"
        message={
          <>
            <ul className="mb-2 space-y-2">
              {hoiLai?.canhBao.map((c, i) => <li key={i}>⚠ {c}</li>)}
            </ul>
            <p className="text-xs text-chu-2">Số 0 ở những ô này là một lệnh thật, không phải "để trống".</p>
          </>
        }
        onConfirm={() => hoiLai && void ghi(hoiLai.patch)}
        onCancel={() => setHoiLai(null)}
      />
    </div>
  );
}

/* ---------------------------------------------------------- khung thẻ + hàng */

/** Thẻ có đầu đề, viền, và các hàng bên trong tự kẻ vạch ngăn. */
function TheCaiDat({
  tieuDe, Icon, phu, children,
}: { tieuDe: string; Icon: ComponentType<{ className?: string }>; phu?: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-the border border-vien bg-nen-1">
      <div className="flex items-center gap-2 border-b border-vien px-3 py-2">
        <Icon className="h-3.5 w-3.5 shrink-0 text-nhan" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-nhan">{tieuDe}</h3>
        {/*
          * ⚠ Ẩn ở màn hẹp — cùng lý do với nhãn nhỏ bên phải hàng lớp: ở 390 px nó đẩy
          *   tiêu đề xuống dòng và làm đầu thẻ cao gấp đôi. dev_device đã gặp khi chụp.
          */}
        {phu && <span className="ml-auto hidden font-mono text-xs text-chu-3 sm:block">{phu}</span>}
      </div>
      <div className="divide-y divide-vien">{children}</div>
    </div>
  );
}

/** Chú giải đi vào đây, KHÔNG chiếm một dòng của mỗi hàng. */
function Hoi({ chu }: { chu?: string }) {
  if (!chu) return null;
  return (
    <span
      title={chu}
      className="inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center
                 rounded-full border border-vien-manh text-xs font-bold leading-none text-chu-3"
    >?</span>
  );
}

/**
 * MỘT HÀNG = công tắc lớp + tên + ô ngưỡng, TẤT CẢ trên cùng một dòng.
 *
 * 🔴 Vì sao ghép: bật một lớp mà quên đặt ngưỡng là lỗi thật (mã đã chặn ở `chan`), và hai
 *   thứ ấy trước đây nằm ở hai chỗ cách nhau nửa màn hình. Đặt cạnh nhau thì người ta thấy
 *   được cả hai vế cùng lúc — bố cục làm phần việc mà một câu cảnh báo đang phải làm.
 */
function HangLop({
  lop, pe, khoa, doiSangPe, o, giaTriGoc, giaTriSua, doiSang, biTuChoi,
}: {
  lop: { bit: number; ten: string; truongNguong: string };
  pe: number; khoa: boolean; doiSangPe: (v: number) => void;
  o: O; giaTriGoc?: number; giaTriSua?: string;
  doiSang: (v: string) => void; biTuChoi?: { xin: number; thucTe: number };
}) {
  /*
   * ⛔ BIT CHẾT ⇒ KHÔNG VẼ CÔNG TẮC. `protectEn` bit 0 (dòng rò) không được firmware đọc bao
   *   giờ (`grep "protect_en & 0x01"` → 0 dòng, dev_device 08/09). Một nút gạt lưu được mà
   *   không tới dây là **nói dối bằng hành vi** — nặng hơn một nhãn sai, vì người dùng tin là
   *   họ vừa LÀM một việc. Cùng hạng `inputEn`/`outputEn` đã bị bắt dừng UI.
   */
  const bitChet = (BIT_CHET_TRONG_PROTECT_EN as readonly number[]).includes(lop.bit);
  const bat = !bitChet && lopDangBat(pe, lop.bit);
  const hienTai = giaTriSua ?? (giaTriGoc === undefined ? '' : String(giaTriGoc));
  return (
    <div className={`flex items-center gap-2.5 px-3 ${bat ? '' : 'opacity-95'}`} style={{ minHeight: 36 }}>
      {bitChet ? (
        <span
          aria-hidden
          title="Lớp này không bật/tắt bằng protectEn — xem chú thích dưới thẻ"
          className="h-[17px] w-[30px] shrink-0 rounded-thuoc border border-dashed border-vien-manh"
        />
      ) : (
        <button
          type="button" role="switch" aria-checked={bat} aria-label={`Bật/tắt ${lop.ten}`}
          disabled={khoa}
          onClick={() => doiSangPe(datLop(pe, lop.bit, !bat))}
          className={`relative h-[17px] w-[30px] shrink-0 rounded-thuoc border transition
                      ${bat ? 'border-tot bg-tot/25' : 'border-vien-manh bg-nen-0'}
                      ${khoa ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
        >
          <span
            className={`absolute top-[2px] h-[11px] w-[11px] rounded-thuoc transition-all
                        ${bat ? 'left-[15px] bg-tot' : 'left-[2px] bg-chu-3'}`}
          />
        </button>
      )}
      <span className={`flex-none basis-[92px] text-[12px] font-semibold sm:basis-[116px]
                        ${bat ? 'text-chu-1' : 'text-chu-3'}`}>{lop.ten}</span>
      <input
        className={`inp ml-auto w-[92px] text-right font-mono tabular-nums ${bat ? '' : 'opacity-60'}`}
        type="number" inputMode="numeric" value={hienTai} disabled={khoa}
        onChange={(e) => doiSang(e.target.value)}
      />
      <span className={`min-w-[26px] text-xs ${bat ? 'text-chu-2' : 'text-chu-3'}`}>{o.donVi}</span>
      <Hoi chu={o.goiY} />
      {biTuChoi && <Badge tone="amber">tủ từ chối</Badge>}
    </div>
  );
}

/**
 * 🔴 SỐ PHA CỦA TỦ — sống trong `protectEn` bit 6-7, KHÔNG phải một trường riêng.
 *
 * ⛔ `0` = **CHƯA ĐẶT**, không phải "0 pha" — nên hiện xám nghiêng, và máy chủ đọc ra 0 thì
 *   coi là CHƯA RÕ: không dựng `PHASE_LOSS`, cũng không đóng nó (L17).
 *
 * ⚠ Mọi phép sửa đi qua `datSoPha()` — nó GIỮ NGUYÊN bit 0..3. Đây đúng là chỗ firmware
 *   từng xoá sạch cấu hình bảo vệ bằng một mặt nạ `& 0x0F` (hai chỗ, đã sửa bên tủ). App
 *   không được đẻ ra chỗ thứ ba; xem `lib/baove.ts` và phép `[51]`.
 */
function SoPhaCuaTu({ pe, khoa, doiSang }: { pe: number; khoa: boolean; doiSang: (v: number) => void }) {
  const n = docSoPha(pe);
  return (
    <div className="flex items-center gap-2.5 px-3" style={{ minHeight: 34 }}>
      <span className="flex-none basis-[120px] text-xs font-semibold uppercase tracking-wide text-chu-2 sm:basis-[152px]">
        Số pha của tủ
      </span>
      <select
        className={`inp ml-auto w-[112px] ${chuaDatSoPha(n) ? LOP_CHUA_RO : ''}`}
        value={String(n)} disabled={khoa}
        onChange={(e) => doiSang(datSoPha(pe, Number(e.target.value) as SoPha))}
      >
        {([3, 2, 1, 0] as SoPha[]).map((v) => (
          <option key={v} value={String(v)}>{NHAN_SO_PHA[v]}</option>
        ))}
      </select>
      <Hoi chu="Máy chủ dùng số này để biết mất pha. Chưa đặt = máy chủ coi là CHƯA RÕ, không dựng cảnh báo mất pha. Số này nằm ở protectEn bit 6-7." />
    </div>
  );
}

/* ------------------------------------------------------------------ ô nhập */

function ONhap({
  o, giaTriGoc, giaTriSua, doiSang, xoaSua, biTuChoi, khoa,
}: {
  o: O; giaTriGoc: number | undefined; giaTriSua: string | undefined;
  doiSang: (v: string) => void; xoaSua: () => void;
  biTuChoi?: { xin: number; thucTe: number }; khoa: boolean;
}) {
  const hienTai = giaTriSua ?? (giaTriGoc === undefined ? '' : String(giaTriGoc));
  const daSua = giaTriSua !== undefined && Number(giaTriSua) !== giaTriGoc;

  /*
   * ⚠ NHÃN TRÁI · Ô PHẢI, và chú giải vào TOOLTIP.
   *   Bản trước dùng `Field`: nhãn TRÊN, gợi ý DƯỚI ⇒ mỗi ô cao ba dòng, và mắt phải dò
   *   zig-zag để so hai giá trị. Ở đây nhãn thẳng một cột bên trái, số thẳng một cột bên
   *   phải — so sánh theo cột là việc mắt làm nhanh nhất.
   * ⛔ `o.goiY` KHÔNG mất: nó vào `?`. Bỏ hẳn thì mất một câu dạy việc (vd "Mặc định 170 —
   *   phải NHỎ HƠN ngưỡng quá áp"), mà để nguyên thì mỗi hàng cao gấp ba.
   */
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-1.5" style={{ minHeight: 34 }}>
      <span className="flex-none basis-[120px] text-xs font-semibold uppercase tracking-wide text-chu-2 sm:basis-[152px]">
        {o.nhan}
      </span>
      <div className="ml-auto flex items-center gap-2">
        {daSua && <Badge tone="blue">sẽ gửi</Badge>}
        {biTuChoi && <Badge tone="amber">tủ từ chối</Badge>}
        {o.chon ? (
          <select className="inp w-[136px]" value={hienTai} disabled={khoa} onChange={(e) => doiSang(e.target.value)}>
            {o.chon.map((c) => <option key={c.gt} value={String(c.gt)}>{c.nhan}</option>)}
          </select>
        ) : (
          <input
            className="inp w-[92px] text-right font-mono tabular-nums" type="number" inputMode="numeric"
            value={hienTai} disabled={khoa} onChange={(e) => doiSang(e.target.value)}
          />
        )}
        {/* Đơn vị cạnh ô nhập: nhỏ hơn + nhạt hơn số, cùng luật 2 với `Metric`. */}
        <span className="min-w-[26px] text-xs text-chu-3">{o.donVi}</span>
        {/*
          * ⛔ SỐ 0 MANG NGHĨA KHÁC — hiện câu chú, KHÔNG đổi con số trong ô.
          *   Ô này là thứ **sẽ được gửi xuống tủ**: vẽ `60` vào chỗ đang lưu `0` là nói dối
          *   về cái sắp gửi, và lần bấm "Ghi" kế tiếp gửi một số người dùng chưa từng gõ.
          * ⚠ Đọc `giaTriGoc` (số của TỦ), không đọc ô đang sửa: câu chú nói về cái tủ đang
          *   giữ, không về cái người dùng vừa gõ dở.
          */}
        {nghiaSoKhong(o.ten, giaTriGoc) && (
          <span className="text-xs font-medium text-canh">{nghiaSoKhong(o.ten, giaTriGoc)}</span>
        )}
        <Hoi chu={o.goiY} />
      </div>
      {(daSua || biTuChoi) && (
        <span className="w-full pl-0 text-right text-xs text-chu-3 sm:pl-[152px]">
          {biTuChoi ? <b className="text-canh">Đã xin {biTuChoi.xin}, tủ giữ {biTuChoi.thucTe}</b> : null}
          {daSua && giaTriGoc !== undefined && (
            <> {biTuChoi ? '· ' : ''}Tủ đang là {giaTriGoc}{' '}
              <button type="button" className="underline hover:text-chu-2" onClick={xoaSua}>hoàn</button>
            </>
          )}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------- bật/tắt từng lớp bảo vệ */

function BatTatBaoVe({
  giaTri, doiSang, khoa, biTuChoi,
}: { giaTri: number; doiSang: (v: number) => void; khoa: boolean; biTuChoi?: { xin: number; thucTe: number } }) {
  return (
    <div className="rounded border p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        Bật/tắt từng lớp bảo vệ
        {biTuChoi && <Badge tone="amber">tủ từ chối</Badge>}
        <span className="text-xs font-normal text-chu-3">protectEn = {giaTri} (0x{giaTri.toString(16).padStart(2, '0')})</span>
      </div>
      <div className="flex flex-wrap gap-4">
        {LOP_BAO_VE.map((l) => {
          const bat = ((giaTri >>> l.bit) & 1) === 1;
          return (
            <label key={l.bit} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox" checked={bat} disabled={khoa}
                onChange={(e) => doiSang(e.target.checked ? giaTri | (1 << l.bit) : giaTri & ~(1 << l.bit))}
              />
              {l.ten}
            </label>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-chu-2">
        Xuất xưởng là tắt hết (0x00). Bật lớp nào thì ngưỡng của lớp đó phải khác 0, nếu không tủ coi như chưa bật.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ lát 2 · giờ */

function Lat2Gio({
  gio, online, coTheGhi, dangDongBo, onDoc, onDongBo,
}: {
  gio: TrangThaiTu | null; online: boolean; coTheGhi: boolean; dangDongBo: boolean;
  onDoc: () => void; onDongBo: () => void;
}) {
  const st = gio?.echo ?? {};
  const tuMs = Number(st.deviceTimeMs ?? 0) || 0;
  const coTz = Object.prototype.hasOwnProperty.call(st, 'tzOffsetMin');
  const tz = Number(st.tzOffsetMin ?? 0) || 0;
  const lech = tuMs && gio ? Math.round((tuMs - gio.gioMayChuMs) / 1000) : null;
  // Lệch ≈ một số nguyên giờ là dấu hiệu lẫn UTC với giờ địa phương, không phải trôi đồng hồ.
  // Phải xét cả hai phía của mốc tròn giờ: lệch 7h thiếu 30 giây vẫn là lẫn múi giờ.
  const du = lech === null ? 0 : Math.abs(lech) % 3600;
  const nghiLanMuiGio = lech !== null && Math.abs(lech) > 1800 && Math.min(du, 3600 - du) < 120;

  return (
    <div className="mt-5 border-t pt-4">
      <SectionTitle
        right={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onDoc} disabled={!online}>Đọc lại</Button>
            <Button onClick={onDongBo} loading={dangDongBo} disabled={!coTheGhi || !online}>
              Đồng bộ giờ theo máy chủ
            </Button>
          </div>
        }
      >
        <span className="flex items-center gap-2">
          <Clock className="h-5 w-5 shrink-0 text-chu-3" />
          Đồng hồ tủ
        </span>
      </SectionTitle>

      {!gio ? (
        <p className="text-sm text-chu-3">Chưa đọc.</p>
      ) : (
        <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div>
            {/*
              * 🔴 SỐ CHÍNH LÀ GIỜ VIỆT NAM — YC-31 mục 2. Người trực ca không nghĩ bằng UTC;
              *   bắt họ tự cộng 7 tiếng mỗi lần nhìn CHÍNH LÀ chỗ sinh ra sai số 7 giờ mà
              *   NV49/NV50 vừa mất hai ngày để dẹp.
              *
              * ⛔ ĐÂY LÀ THAY ĐỔI HIỂN THỊ, KHÔNG PHẢI THAY ĐỔI DÂY. Chốt NV49 giữ nguyên và
              *   câu giải thích dưới ô vẫn còn: *"đường truyền LUÔN LÀ UTC; app không bao giờ
              *   gửi giờ địa phương đã cộng sẵn."* Dòng UTC vẫn in ra, nhỏ và xám — bỏ hẳn
              *   thì mất mất đường đối chiếu với log máy chủ.
              *
              * ⚠ Cộng theo `tzOffsetMin` TỦ BÁO, không theo múi giờ của trình duyệt: đây là
              *   giờ CỦA TỦ, và tủ có thể đang đặt sai múi — đúng thứ ta cần nhìn thấy.
              *   Tủ chưa báo `tzOffsetMin` ⇒ KHÔNG đoán +7: nói thẳng là chưa biết.
              */}
            <div className="text-xs text-chu-2">Giờ tủ</div>
            {tuMs && coTz ? (
              <>
                <div className="font-mono text-base font-semibold tabular-nums">
                  {new Date(tuMs + tz * 60_000).toISOString().replace('T', ' ').slice(0, 19)}
                  <span className="ml-1.5 text-xs font-normal text-chu-2">
                    ({tz >= 0 ? '+' : '-'}{String(Math.floor(Math.abs(tz) / 60)).padStart(2, '0')}:
                    {String(Math.abs(tz) % 60).padStart(2, '0')})
                  </span>
                </div>
                <div className="font-mono text-xs tabular-nums text-chu-3">
                  UTC {new Date(tuMs).toISOString().replace('T', ' ').slice(0, 19)}
                </div>
              </>
            ) : (
              <div>
                {tuMs
                  ? <>
                      <span className="font-mono tabular-nums">UTC {new Date(tuMs).toISOString().replace('T', ' ').slice(0, 19)}</span>
                      <span className="ml-1.5 text-xs text-chu-3">(tủ chưa báo múi giờ — chưa quy ra giờ địa phương được)</span>
                    </>
                  : '—'}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-chu-2">Múi giờ tủ đang dùng</div>
            <div>
              {/*
                * ➕ `GD-10` việc 3 — ĐỌC ĐƯỢC, chứ không phải đúng-nhưng-phải-tự-chia.
                *   Bản cũ in *"+420 phút (Việt Nam)"*: người trực phải chia 60 mới biết là +7h,
                *   và chữ "(Việt Nam)" thì so với một số `420` gõ tay ngay tại chỗ này.
                * ⛔ `MUI_GIO_VN_PHUT` là MỘT nguồn cho cả lời gọi `/api/lenh/gio` lẫn câu chữ
                *   này — hai chỗ ấy nói về cùng một sự thật nên phải đọc cùng một hằng.
                * ⚠ `coTz` vẫn là vế ĐẦU và không đổi: tủ CHƯA BÁO thì nói chưa báo, không in
                *   `GMT+00:00` — `0` là một múi giờ có thật (Luật số 0).
                */}
              {coTz
                ? `${cauMuiGio(tz)}${tz === MUI_GIO_VN_PHUT ? ' (Việt Nam)' : ''}`
                : '— (tủ chưa báo)'}
            </div>
          </div>
          <div>
            <div className="text-xs text-chu-2">Lệch so với máy chủ</div>
            <div className={lech !== null && Math.abs(lech) > 60 ? 'font-medium text-canh' : ''}>
              {lech === null ? '—' : `${lech > 0 ? '+' : ''}${lech} giây`}
            </div>
          </div>
        </div>
      )}

      {nghiLanMuiGio && (
        <p className="mt-2 rounded bg-canh-nen p-2 text-sm text-canh">
          ⚠ Lệch xấp xỉ tròn giờ ({(lech! / 3600).toFixed(1)} h). Đây là dấu hiệu <b>lẫn UTC với giờ
          địa phương</b>, không phải đồng hồ trôi — đồng bộ lại bao nhiêu lần cũng vẫn lệch nếu tủ
          hiểu sai quy ước. Xem múi giờ tủ báo ở ô bên cạnh.
        </p>
      )}

      <p className="mt-2 text-xs text-chu-2">
        Đường truyền luôn là <b>UTC</b>; tủ tự cộng lệch múi giờ. App không bao giờ gửi giờ địa phương
        đã cộng sẵn. Tủ từ chối mốc ngoài khoảng 2020–2036.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------ trạng thái sống */

function TrangThaiSong({ live, onDoc, online }: { live: LiveState | null; onDoc: () => void; online: boolean }) {
  const s = live?.echo ?? {};
  const so = (v: unknown) => Number(v ?? 0) || 0;          // proto3 trần ⇒ đọc THEO GIÁ TRỊ
  /*
   * ══════════════════════════════════════════════════════════════════════════════════════
   * 🔴 `212.4687957763672 V` — 13 chữ số thập phân lee nhìn thấy ĐẺ RA TỪ ĐÂY: `so()` trả
   *   một `number` thô và `Metric` in nguyên si. Đó là **nhiễu của `float32`**, không phải
   *   số đo: `float32` chỉ giữ ~7 chữ số có nghĩa, phần đuôi là bản khai triển sang `double`.
   *
   * ⚠ SỐ CHỮ SỐ THẬP PHÂN = ĐỘ PHÂN GIẢI CỦA MÁY ĐO, không phải của kiểu dữ liệu (YC-31
   *   mục 3, cùng họ `0.000 A` của NV5). Đo firmware, và nó chia đôi câu trả lời:
   *      KWS      `bsp_modbus.cpp:729`  `kws[0x000E] / 10.0f`  ⇒ phân giải **0,1 V** — CHẮC
   *      MFM383A  `bsp_modbus.cpp:664`  đọc THẲNG `float` IEEE-754, KHÔNG có số chia
   *                                     ⇒ firmware KHÔNG nói gì về độ phân giải thật
   *
   * ⇒ Lấy **1 chữ số**: với KWS đó là con số trung thực nhất *(chữ số thứ hai luôn là bịa)*,
   *   và nó khớp với `CabinetDetail` vốn đã dùng `soHoac(v, 1)` — cùng một đại lượng hiện
   *   hai kiểu cũng là một lỗi. Với MFM383A thì 1 chữ số là chọn AN TOÀN, không phải chọn
   *   ĐÚNG: cần datasheet mới biết. Ghi thành khoản nợ ở `[54]`, không giấu vào một con số.
   * ══════════════════════════════════════════════════════════════════════════════════════
   */
  const soDo = (v: unknown, chuSo = 1) => {
    const n = Number(v ?? 0);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 10 ** chuSo) / 10 ** chuSo;
  };
  const co = (v: unknown) => v === true;
  const out = so(s.outputState);
  /*
   * YC-34 vòng 2 — cửa DUY NHẤT quyết vẽ hay không cho cả ảnh chụp này.
   * ⛔ `=== true`, không phải `!== false`: máy chủ đời cũ không trả `soDo` ⇒ rơi về KHÔNG VẼ.
   */
  /*
   * ⚠ HAI VẾ (YC-35). Vế thứ hai là chỗ dễ quên nhất: ảnh chụp này KHÔNG tự hỏi lại — nó nằm
   *   trong `useState` cho tới khi ai đó bấm "Đọc lại". Chấm một lần rồi thôi thì đúng lúc
   *   nhận, và sai mọi lúc sau đó.
   *   ⛔ `live?.soDo` làm mốc: đối tượng ấy đổi tham chiếu mỗi lần đọc lại ⇒ đồng hồ về 0.
   */
  const conHanLive = useConTrongHan(live?.soDo?.hanConLaiMs, live?.soDo);
  const veDuocLive = live?.soDo?.ve === true && conHanLive;
  const cauLive = live?.soDo === undefined
    ? 'Chưa rõ ảnh chụp còn mới hay không — không vẽ.'
    : 'cau' in live.soDo
      ? live.soDo.cau
      /* `ve: true` mà tới đây ⇒ ảnh chụp đã HẾT HẠN ngay trên màn hình. */
      : CAU_HET_HAN;
  const cutBits = so(s.safetyCutBits);
  /* `YC-48` — đọc một lần, dùng cho cả cửa lẫn câu lý do. Một sự thật, một chỗ đọc. */
  const tuoiSoDo = docSoConTuoi(s.soConTuoi);
  const lop = LOP_BAO_VE.filter((l) => ((cutBits >>> l.bit) & 1) === 1).map((l) => l.ten);

  return (
    <div className="mt-5 border-t pt-4">
      <SectionTitle right={<Button variant="ghost" onClick={onDoc} disabled={!online}>Đọc lại</Button>}>
        Trạng thái sống
      </SectionTitle>
      {!live ? (
        <p className="text-sm text-chu-3">Chưa đọc.</p>
      ) : !veDuocLive ? (
        /*
         * ═══════════════════════════════════════════════════════════════════════════════════
         * ⛔ CẢ KHUNG NÀY LÀ MỘT ẢNH CHỤP, KHÔNG PHẢI MỘT DÒNG CHẢY. Đọc một lần rồi nó nằm
         *   lại trên màn hình: điện áp, ba ngõ ra, cổng an toàn, ELR — tất cả cùng một `atMs`.
         *   Tủ chết sau đó thì cả bảng vẫn đứng nguyên, trông y như vừa đọc xong.
         *
         * ⇒ Cũ thì giấu CẢ BẢNG, không giấu một ô. Giấu một ô là nói rằng những ô còn lại vẫn
         *   tươi — mà chúng cũng cũ đúng bằng ngần ấy.
         * ═══════════════════════════════════════════════════════════════════════════════════
         */
        <div className="rounded-lg border border-vien bg-nen-2 px-3 py-3 text-sm">
          <p className="font-semibold text-chu-1">Chưa có số liệu</p>
          <p className="mt-1 text-chu-2">{cauLive}</p>
          <p className="mt-1 text-xs text-chu-3">
            Ảnh chụp cũ đã bỏ đi thay vì vẽ lại — bấm “Đọc lại” để hỏi tủ một lần nữa.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <div className="text-xs text-chu-2">Điện áp</div>
            {/*
              * ═══════════════════════════════════════════════════════════════════════════
              * 🔴 `YC-48` — CỬA ĐỘ TƯƠI. `soConTuoi != 1` ⇒ **KHÔNG VẼ SỐ**, bất kể số bằng
              *   bao nhiêu. Đây là chỗ DUY NHẤT chặn được: kho firmware không có `has_`, nên
              *   dưới tầng này `voltA = 0` không phân biệt được *"đo được 0 V"* với *"không
              *   có số"*.
              * ⛔ Và KHÔNG để trống suông — một ô trống bị đọc thành *"tủ không có số"*, mà
              *   sự thật có thể là *"tủ chưa khai độ tươi"*. Câu lý do đi kèm, bắt buộc.
              * ⚠ Giá phải trả, nói ra: mọi tủ CHƯA NẠP `P-3` sẽ không hiện điện áp ở ô này
              *   cho tới khi nạp. Hỏng về phía "không vẽ một số không chứng minh được".
              * ═══════════════════════════════════════════════════════════════════════════
              */}
            {veDuocSoDo(tuoiSoDo) ? (
              <>
                {/* ⛔ Trước đây ghép tay `{so(v)} V` ba lần — ba chỗ ghép, ba cách vẽ, và không
                    chỗ nào vẽ được mức "chưa có số liệu". Nay đi qua `Metric`. */}
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span>A <Metric giaTri={soDo(s.voltA)} donVi="V" coNho /></span>
                  <span>B <Metric giaTri={soDo(s.voltB)} donVi="V" coNho /></span>
                  <span>C <Metric giaTri={soDo(s.voltC)} donVi="V" coNho /></span>
                </div>
                <div className="text-xs text-chu-3">
                  Pha dưới 50 V coi là không có điện
                  {moTaDoTuoiMs(s.doTuoiMs) ? ` · tủ đọc ${moTaDoTuoiMs(s.doTuoiMs)}` : ''}
                </div>
              </>
            ) : (
              <div className="text-xs leading-relaxed text-canh">{cauKhongVeSoDo(tuoiSoDo)}</div>
            )}
          </div>
          <div>
            {/*
              * ⛔ NHÃN CŨ VIẾT *"số đọc thô"* — VÀ CÂU ẤY SAI. dev_device tự đính chính ở [G1]
              *   (`kapp_v2.proto:326`): `outputState` dựng từ `cache_wb.output_state[]`, đúng
              *   mảng mà đường điều khiển ghi vào khi nó RA LỆNH. Không ai đọc tiếp điểm.
              *
              *   *"(theo lệnh)"* cũng chưa đủ đúng — người đọc sẽ hiểu là "theo lệnh tôi vừa
              *   bấm". Chữ dev_device chốt là **"tủ tự khai"**: nói cùng lúc AI nói, và rằng
              *   KHÔNG CÓ PHÉP ĐO. Ngày board có ngõ vào đọc tiếp điểm phụ thì bỏ ngoặc đi.
              */}
            <div className="text-xs text-chu-2">Ba ngõ ra (tủ tự khai)</div>
            <div>{[0, 1, 2].map((i) => `${i + 1}:${((out >>> i) & 1) ? 'ĐÓNG' : 'mở'}`).join('  ')}</div>
          </div>
          <div>
            <div className="text-xs text-chu-2">Cổng an toàn</div>
            <div>
              {co(s.safetyCut)
                ? <span className="text-nguy font-medium">ĐANG CẮT{lop.length ? ` — ${lop.join(', ')}` : ' — không nói được lý do'}</span>
                : 'không cắt'}
            </div>
            {/*
              * ═══════════════════════════════════════════════════════════════════════════
              * 🔴 QUÁ DÒNG KHÔNG TỰ ĐÓNG LẠI — YC-38, dev_device chốt 07/09.
              *
              * Quá áp / thấp áp tự đóng lại sau `ovUvChoDongS` (~60 s). Cổng QUÁ DÒNG thì
              * **khoá cứng sau 3 lần thử**: `safetyCut` giữ `1` mãi cho tới khi có người đổi
              * cấu hình hoặc tắt nguồn.
              *
              * ⛔ Hai thứ ấy hiện ra y hệt nhau nếu chỉ viết "ĐANG CẮT" — và người trực sẽ
              *   ngồi đợi một thứ không bao giờ tự về. Chữ phải nói ra sự khác nhau, vì đây
              *   là chỗ duy nhất người ta đọc được nó.
              *
              * ⚠ Tôi KHÔNG viết "đã khoá": số lần thử KHÔNG có trên dây, nên tôi không biết
              *   tủ đã hết 3 lần hay chưa. Câu chỉ khai đúng thứ đọc được từ hợp đồng — lớp
              *   này không tự đóng lại — chứ không khai một trạng thái chưa ai đo (L62).
              * ═══════════════════════════════════════════════════════════════════════════
              */}
            {co(s.safetyCut) && ((cutBits >>> 3) & 1) === 1 && (
              <div className="mt-1 text-xs text-nguy">
                Lớp quá dòng <b>KHÔNG tự đóng lại</b> — khoá cứng sau 3 lần thử. Cần người ra
                xem: đổi cấu hình hoặc tắt nguồn tủ thì mới mở.
              </div>
            )}
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs text-chu-2">ELR</div>
            <div>
              {co(s.elrOnline)
                ? (
                  <span className="flex flex-wrap items-baseline gap-x-3">
                    <span>tủ nhìn thấy — rò <Metric giaTri={so(s.elrLeakMa)} donVi="mA" coNho /></span>
                    <span>· ngưỡng trong ELR <Metric giaTri={so(s.elrSetMa)} donVi="mA" coNho /></span>
                    <span>· trễ <Metric giaTri={so(s.elrDelayDs)} donVi="×0,1 s" coNho /></span>
                  </span>
                )
                : 'tủ KHÔNG nhìn thấy ELR'}
            </div>
            {!co(s.elrOnline) && (
              <div className="text-xs text-chu-3">
                Đây KHÔNG phải "mất bảo vệ" — ELR cắt bằng mạch riêng, không đi qua tủ.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
